// ui/src/hooks/useCapabilities.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { CreateCapabilityRequest, UpdateCapabilityRequest } from '../types';

// Query keys
export const capabilityKeys = {
  all: ['capabilities'] as const,
  list: () => [...capabilityKeys.all, 'list'] as const,
  detail: (id: string) => [...capabilityKeys.all, 'detail', id] as const,
};

// List capabilities
export function useCapabilities() {
  return useQuery({
    queryKey: capabilityKeys.list(),
    queryFn: () => api.listCapabilities(),
  });
}

// Create capability
export function useCreateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCapabilityRequest) => api.createCapability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

// Update capability
export function useUpdateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCapabilityRequest }) =>
      api.updateCapability(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

// Delete capability
export function useDeleteCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteCapability(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}
